import { useEffect } from "react";
import {
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiLink,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import * as mdi from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { WorkOrderTemplateModal } from "./WorkOrderTemplateModal";
import { useManageOrderTemplateViewModel } from "./useManageWorkOrderTemplateViewModel";

export function ManageWorkOrderTemplate() {
  const router = useRouter();

  const {
    workOrderTemplates,
    trades,
    isAddModalVisible,
    isDeleteModalVisible,
    isLoading,
    selectedWorkOrderTemplate,
    loadWorkOrderTemplates,
    handleAddWorkOrderTemplate,
    toggleAddModal,
    openDeleteModal,
    onDelete,
    closeDeleteModal,
    getTrades,
  } = useManageOrderTemplateViewModel();

  const { hasPermission } = usePermissions();

  const theme = useTheme();

  useEffect(() => {
    loadWorkOrderTemplates();
    getTrades();
  }, []);

  const orderFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
      renderCell: hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.UPDATE)
        ? ({ row: { id, index }, value }) => (
            <MiLink
              key={id}
              title={value}
              to={`/settings/order-management/work-order-types/${id}/details`}
              data-testid={`order-template-link-${index}`}
            />
          )
        : null,
    },
    {
      headerName: "Abbr.",
      field: "shortName",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      headerName: "Color",
      field: "color",
      renderCell: ({ value }) => <input type="color" value={value} disabled />,
    },
  ];

  const getRowActions = (row) => [
    {
      text: BUTTON_LABELS.DELETE,
      onClick: openDeleteModal(row),
      renderIcon: () => (
        <MiIcon
          path={mdi[ICONS_WEB.TRASH_CAN]}
          color={theme.palette.red.main}
        />
      ),
    },
  ];
  const customOnBack = () => {
    router.navigate(`/settings/order-management`);
  };
  return (
    <>
      <MiDetailPageHeader
        title={ENTITY_NAMES.WORK_ORDER_TEMPLATES}
        titleIcon={mdi[ICONS_WEB.CHEVRON_LEFT]}
        loading={isLoading}
        customOnBack={customOnBack}
        actionComponent={
          hasPermission(PERMISSIONS.WORK_ORDER_TEMPLATE.CREATE) && (
            <MiButton
              startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
              variant={BUTTON_VARIANTS.CONTAINED}
              onClick={toggleAddModal}
              data-testid="add-work-order-template"
              title={`Add ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`}
            />
          )
        }
      />
      <MiBox>
        <MiList
          columns={orderFieldset}
          data={workOrderTemplates}
          paginationMode={PAGINATION.CLIENT}
          loading={isLoading}
          getRowActions={
            hasPermission(PERMISSIONS.ORDER_TEMPLATE.DELETE) && getRowActions
          }
          data-testid="order-templates-table"
        />
      </MiBox>
      <WorkOrderTemplateModal
        open={isAddModalVisible}
        title={`Add ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`}
        trades={trades}
        buttonTitle={`Add ${ENTITY_NAMES.WORK_ORDER_TEMPLATE}`}
        onClose={toggleAddModal}
        onSubmit={handleAddWorkOrderTemplate}
        loading={isLoading}
      />
      <MiModalConfirm
        title="Confirm Delete"
        description={`Are you sure you want to delete: ${selectedWorkOrderTemplate.name}?`}
        open={isDeleteModalVisible}
        handlePositive={onDelete}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={closeDeleteModal}
      />
    </>
  );
}
